import React from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import DeleteIcon from '@mui/icons-material/Delete'

export const emptySource = () => ({
    url: '',
    source: '',
    publication_data: '',
    pub_date: '',
})

export const validateSources = (sources) => {
    if (!sources || sources.length === 0) return { ok: false, message: 'At least one source is required' }
    for (const s of sources) {
        if (!s.source || s.source.trim().length === 0) {
            return { ok: false, message: 'Each source must have a source name' }
        }
        if (s.url && s.url.length > 0) {
            try { new URL(s.url) } catch {
                return { ok: false, message: 'Please enter a valid URL for each source' }
            }
        }
    }
    return { ok: true }
}

const TerminologySourcesEditor = ({ sources, onChange }) => {
    const updateField = (index, field, value) => {
        const next = sources.map((s, i) => i === index ? { ...s, [field]: value } : s)
        onChange(next)
    }

    const addSource = () => {
        onChange([...sources, emptySource()])
    }

    const removeSource = (index) => {
        onChange(sources.filter((_, i) => i !== index))
    }

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Sources</Typography>
            {sources.map((src, index) => (
                <Box
                    key={index}
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        mb: 2,
                        position: 'relative',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Source #{index + 1}
                        </Typography>
                        <IconButton
                            onClick={() => removeSource(index)}
                            disabled={sources.length <= 1}
                            size="small"
                            aria-label="Remove source"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                value={src.source || ''}
                                onChange={e => updateField(index, 'source', e.target.value)}
                                label="Source"
                                required
                                helperText="Author, organization, or publication"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                value={src.url || ''}
                                onChange={e => updateField(index, 'url', e.target.value)}
                                label="URL (Optional)"
                                type="url"
                                helperText="Link to external resource or reference"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                value={src.pub_date || ''}
                                type="date"
                                onChange={e => updateField(index, 'pub_date', e.target.value)}
                                label="Publication Date (Optional)"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                multiline
                                rows={1}
                                value={src.publication_data || ''}
                                onChange={e => updateField(index, 'publication_data', e.target.value)}
                                label="Publication Data (Optional)"
                                helperText="Journal, volume, pages, etc."
                            />
                        </Grid>
                    </Grid>
                </Box>
            ))}
            <Button
                onClick={addSource}
                startIcon={<AddCircleIcon />}
                variant="outlined"
                size="small"
            >
                Add Source
            </Button>
        </Box>
    )
}

export default TerminologySourcesEditor
